'use client';

import React, { useState } from 'react';
import { IWebcamItem } from '@/api/region/types';
import { IRelatedNews, IResortPageInfo, IResortPathInfo } from '@/api/resort/types';
import { ILightboxImage, Lightbox } from '@/components/common/Lightbox';
import { PageBody } from '@/components/common/PageBody';
import { ResortWebcam } from '@/components/common/ResortWebcam';
import { Badge } from '@/components/ui/badge';
import { SeoSection } from '@/components/common/SeoSection';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import { RegularAd } from '@/tracking/ads/components/RegularAd';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { IResortLayout } from '@/page-components/resort/_layout';
import PlanYourTrip from '@/components/common/PlanYourTrip/ResortWidget';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { Hero400Error } from '@/components/400';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { useAnalytics } from '@/tracking/analytics';
import { formatAgo } from '@/util/formatAgo';
import { useIsMobile } from '@/util/useIsMobile';

export type ResortCamerasPageProps = {
  resort: IResortPageInfo,
  cameras: Array<IWebcamItem> | null,
  featured: IWebcamItem | null,
  pathInfo: IResortPathInfo,
  relatedNews: IRelatedNews | null,
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-cams-page';
export const TEMPLATE_ID = '2.2.3';
export const TEMPLATE_NAME = 'Resort Cams';

const safeTimestamp = (date: string | undefined) => {
  if (!date) return undefined;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return undefined;
  return formatAgo(d) as string;
};

export default function ResortCamerasPage({
  resort,
  cameras,
  featured,
  pathInfo,
  relatedNews,
}: ResortCamerasPageProps) {
  const { t } = useOTSTranslation('common');
  const a = useAnalytics();
  const isMobile = useIsMobile();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (!resort) {
    return <Hero400Error light />;
  }

  // Merge the featured cam in at the top of the list so the data
  // contract from the API (separate featured + cameras arrays) still
  // works while we render a single grid. De-dupe by uuid in case the
  // API includes the featured cam in both.
  const merged: IWebcamItem[] = [];
  if (featured) merged.push(featured);
  (cameras ?? []).forEach((c) => {
    if (!merged.some((m) => m.uuid === c.uuid)) merged.push(c);
  });

  // One Lightbox serves the whole grid; derived from the same `merged`
  // array so indices always line up with the cards.
  const lightboxImages: ILightboxImage[] = merged.map((cam) => {
    const ts = safeTimestamp(cam.date);
    return {
      src: cam.image || cam.thumbnail,
      alt: cam.title,
      title: ts ? `${cam.title} — ${ts}` : cam.title,
    };
  });

  const openCam = (index: number) => {
    // Desktop only: on touch devices the cards stay non-interactive
    // (same handler-level gate as TrailMap, safe against hydration).
    if (isMobile) return;
    setLightboxIndex(index);
    a.expandImage({ content: 'Webcam' });
  };

  return (
    <>
      <Main>
        <PageBody>
          <SeoSection title={t('resort.cams.seoTitle', { resort: resort.title })}>
            {t('resort.cams.seoText', { resort: resort.title })}
          </SeoSection>
          {!!merged.length && (
            <div className="grid grid-cols-1 gap-x-5 gap-y-7 md:grid-cols-2">
              {merged.map((cam, index) => (
                <ResortWebcam
                  key={cam.uuid}
                  caption={cam.title}
                  image={cam.thumbnail || cam.image}
                  timestamp={safeTimestamp(cam.date)}
                  videoStream={cam.videoStream}
                  webcamType={cam.webcamType}
                  onClick={() => openCam(index)}
                  badge={cam.videoStream ? (
                    <Badge variant="destructive" className="uppercase">
                      {t('app.webcam.live')}
                    </Badge>
                  ) : undefined}
                />
              ))}
            </div>
          )}
          {lightboxIndex !== null && (
            <Lightbox
              onClose={() => setLightboxIndex(null)}
              images={lightboxImages}
              index={lightboxIndex}
            />
          )}
          <RegularAd isFirst />
          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={resort.title}
          />
          <PlanYourTrip
            resort={pathInfo}
            hasSkiRental={pathInfo.hasSkiRental}
            hasFlights={pathInfo.hasFlights}
            hasCarRental={pathInfo.hasCarRental}
            liftsUrl={resort.liftsUrl}
            rentalUrl={resort.rentalUrl}
            lessonsUrl={resort.lessonsUrl}
            isPartner={resort.isPartner}
            resortCountry={resort.region?.country_code}
          />
        </PageBody>
      </Main>
      <Side>
        <RelatedArticles
          relatedNewsList={relatedNews}
        />
        <SidebarAd id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

// fetchProps moved to ./cams.server.ts so the App Router RSC page can import
// the data fetcher without pulling in this 'use client' bundle.
